import React, { useEffect, useState, useCallback } from 'react';
import { twMerge } from 'utilities/src/twMerge';
import useScrollDirection from 'ui/src/hooks/useScrollDirection';

import HeaderContext from './_context';

import { useHeader } from './hooks/useHeader';

import useHeaderThemes from './themes';
import { HeaderSizesEnum, type HeaderSizesType } from './types/_size';
import { type HeaderThemeProps } from './types/_theme';

export type HeaderContainerThemeProps = HeaderThemeProps & {
  size?: HeaderSizesType;
  enableHeaderBehavior?: boolean;
  animatedHeader?: boolean;
  classes?: string;
};

export type HeaderContainerProps = HeaderContainerThemeProps & {
  children: React.ReactNode;
};

function HeaderContainer({
  children,
  bg,
  size = 'Medium',
  enableHeaderBehavior = false,
  animatedHeader = true,
  classes,
}: HeaderContainerProps) {
  const headerContextValue = useHeader();

  const {
    headerSizesClasses,
    headerClasses,
    headerBehaviorClasses,
    headerContainerClasses,
    headerContainerThemeClasses,
    headerColorClasses,
    headerShadowClasses,
    headerInvisibleClasses,
  } = useHeaderThemes({ bg });

  const headerContainerSizesClasses = headerSizesClasses.get(HeaderSizesEnum[size]);

  const [scrolled, setScrolled] = useState(false);
  const { isScrollingDown } = useScrollDirection();

  const onScroll = useCallback(() => {
    const { scrollY } = window;

    if ((scrolled && !scrollY) || (!scrolled && scrollY)) {
      setScrolled(window.scrollY !== 0);
    }
  }, [scrolled]);

  useEffect(() => {
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  return (
    <HeaderContext.Provider value={headerContextValue}>
      <header
        className={twMerge(
          headerContainerSizesClasses,
          headerClasses,
          { [headerBehaviorClasses]: enableHeaderBehavior || animatedHeader },
          headerColorClasses,
          {
            [headerInvisibleClasses]: isScrollingDown && !headerContextValue.isMobileMenuOpen && animatedHeader,
            [headerShadowClasses]: scrolled && animatedHeader,
          },
          classes,
        )}
      >
        <div className={twMerge(headerContainerClasses, headerContainerThemeClasses)}>{children}</div>
      </header>
    </HeaderContext.Provider>
  );
}

export default HeaderContainer;
